import React from 'react';
import { Box, Container, Typography, Button, Grid } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SecurityIcon from '@mui/icons-material/VerifiedUserOutlined';
import SupportIcon from '@mui/icons-material/SupportAgentOutlined';
import SpeedIcon from '@mui/icons-material/SpeedOutlined';
import { landingColors } from '../../../theme/landingTheme';

interface CTASectionProps {
  onRequestDemo: () => void;
}

const trustIndicators = [
  {
    icon: SecurityIcon,
    title: 'Enterprise Security',
    description: 'Bank-level encryption',
  },
  {
    icon: SupportIcon,
    title: '24/7 Support',
    description: 'Always here to help',
  },
  {
    icon: SpeedIcon,
    title: 'Lightning Fast',
    description: 'Built for performance',
  },
];

export const CTASection: React.FC<CTASectionProps> = ({ onRequestDemo }) => {
  return (
    <Box
      component="section"
      sx={{
        py: { xs: 10, md: 16 },
        position: 'relative',
        background: `
          linear-gradient(180deg, ${landingColors.backgroundAlt} 0%, ${landingColors.background} 100%)
        `,
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            textAlign: 'center',
            p: { xs: 4, md: 8 },
            borderRadius: '32px',
            background: landingColors.surface,
            border: `1px solid ${landingColors.border}`,
            boxShadow: '0 24px 64px rgba(15, 23, 42, 0.08)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Background decorations */}
          <Box
            sx={{
              position: 'absolute',
              top: -100,
              right: -100,
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${landingColors.primaryMuted} 0%, transparent 70%)`,
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -100,
              left: -100,
              width: 250,
              height: 250,
              borderRadius: '50%',
              background: `radial-gradient(circle, rgba(139, 92, 246, 0.05) 0%, transparent 70%)`,
              pointerEvents: 'none',
            }}
          />

          <Box sx={{ position: 'relative', zIndex: 1 }}>
            {/* Heading */}
            <Typography
              variant="overline"
              sx={{
                display: 'inline-block',
                mb: 2,
                px: 2,
                py: 0.5,
                backgroundColor: landingColors.primaryMuted,
                borderRadius: '6px',
              }}
            >
              Get Started Today
            </Typography>

            <Typography variant="h2" sx={{ mb: 2 }}>
              Ready to Transform Your{' '}
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(135deg, ${landingColors.primary} 0%, ${landingColors.secondary} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Store Management?
              </Box>
            </Typography>

            <Typography
              variant="body1"
              sx={{
                mb: 4,
                fontSize: { xs: '1rem', md: '1.125rem' },
                color: landingColors.textSecondary,
                maxWidth: 480,
                mx: 'auto',
              }}
            >
              Join hundreds of successful retailers who have streamlined their operations
              with Nexera. Get a personalized demo today.
            </Typography>

            {/* CTA Buttons */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                justifyContent: 'center',
                mb: 6,
              }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={onRequestDemo}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  background: `linear-gradient(135deg, ${landingColors.primary} 0%, ${landingColors.primaryDark} 100%)`,
                  boxShadow: '0 8px 32px rgba(99, 102, 241, 0.35)',
                  px: 5,
                  py: 2,
                  fontSize: '1rem',
                  '&:hover': {
                    boxShadow: '0 12px 40px rgba(99, 102, 241, 0.45)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Request Demo
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="/login"
                sx={{
                  borderColor: landingColors.border,
                  borderWidth: 2,
                  color: landingColors.textPrimary,
                  px: 5,
                  py: 2,
                  fontSize: '1rem',
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: landingColors.primary,
                    backgroundColor: landingColors.primaryMuted,
                  },
                }}
              >
                Sign In
              </Button>
            </Box>

            {/* Trust Indicators */}
            <Grid container spacing={3} justifyContent="center">
              {trustIndicators.map((indicator, index) => {
                const Icon = indicator.icon;
                return (
                  <Grid item xs={12} sm={4} key={index}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5,
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: 24,
                          color: landingColors.primary,
                        }}
                      />
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            color: landingColors.textPrimary,
                            lineHeight: 1.2,
                          }}
                        >
                          {indicator.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: landingColors.textMuted,
                          }}
                        >
                          {indicator.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default CTASection;
